import { cn } from "@/utils/utils";

/** The picture if there is one, initials if not. Size and shape come from the caller. */
export function Avatar({
  src,
  name,
  className,
}: {
  src: string | null | undefined;
  name: string;
  className?: string;
}) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  return src ? (
    <img
      src={src}
      alt=""
      className={cn("bg-brand-700 size-9 shrink-0 rounded-full object-cover", className)}
    />
  ) : (
    <span
      aria-hidden
      className={cn(
        "bg-brand-700 text-subhead grid size-9 shrink-0 place-items-center rounded-full font-semibold text-white",
        className,
      )}
    >
      {initials || "?"}
    </span>
  );
}

/**
 * Turn a chosen file into a square 160px JPEG data URL, cropped to cover.
 * Done here so the server only ever stores a thumbnail, whatever was picked.
 */
export const readAvatar = (file: File) => readImage(file, 160);

/** A chosen file as a square JPEG data URL of the given size, cropped to cover. */
export function readImage(file: File, size: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const scale = Math.max(size / image.width, size / image.height);
      const width = image.width * scale;
      const height = image.height * scale;
      canvas
        .getContext("2d")
        ?.drawImage(image, (size - width) / 2, (size - height) / 2, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file is not an image we can read."));
    };
    image.src = url;
  });
}
